"use client";

import { EmojiReactionPicker } from "./EmojiReactionPicker";
import { Emoji } from "@/components/ui/Emoji";

/**
 * The two-button row under a post: react, and comment.
 *
 * SHARED BY THE CARD AND THE LIGHTBOX, which drew the same grid twice — same
 * columns, same icons, same "Reageret" state, same class strings. The lightbox
 * copy is how that surface came to differ in small ways nobody chose.
 *
 * `CommentItem` is deliberately NOT a caller. A comment's actions are inline
 * text ("Synes godt om · Svar · Anmeld"), not an iconed grid — a different
 * control that happens to share two words, and folding it in here would mean a
 * component with a mode switch for every difference.
 */

const BUTTON =
  "w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors";

function ThumbIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}

function CommentIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function PostActionRow({
  groupId,
  currentReaction,
  currentReactionUrl,
  onReact,
  onComment,
  canReact = true,
  canComment = true,
  onBlocked,
}: {
  groupId: number;
  currentReaction: string | null;
  /** The image for the viewer's own reaction, if it is a custom emoji. */
  currentReactionUrl: string | null;
  onReact: (emoji: string, url?: string | null) => void;
  onComment: () => void;
  /**
   * A non-member sees the buttons but cannot act (D11 — the server's answer,
   * never guessed). Both then route to `onBlocked` instead, which is what draws
   * the join prompt. The lightbox is only reachable from a post the viewer can
   * already see and leaves these true.
   */
  canReact?: boolean;
  canComment?: boolean;
  onBlocked?: () => void;
}) {
  const reactButton = (
    <button
      className={`${BUTTON} ${
        currentReaction
          ? "text-primary hover:bg-indigo-50 dark:hover:bg-indigo-950"
          : "text-muted-foreground hover:bg-surface-2"
      }`}
    >
      {currentReaction ? (
        <>
          <Emoji emoji={currentReaction} url={currentReactionUrl} size="md" /> Reageret
        </>
      ) : (
        <>
          <ThumbIcon /> Synes godt om
        </>
      )}
    </button>
  );

  return (
    <div className="mx-gutter border-t border-border py-1 grid grid-cols-2">
      {canReact ? (
        <EmojiReactionPicker groupId={groupId} onSelect={onReact}>
          {reactButton}
        </EmojiReactionPicker>
      ) : (
        <button type="button" onClick={onBlocked} className={`${BUTTON} text-muted-foreground hover:bg-surface-2`}>
          <ThumbIcon /> Synes godt om
        </button>
      )}
      <button
        type="button"
        onClick={canComment ? onComment : onBlocked}
        className={`${BUTTON} text-muted-foreground hover:bg-surface-2`}
      >
        <CommentIcon /> Kommenter
      </button>
    </div>
  );
}
